"use client";

/**
 * Общая труба RUM-метрик (KAN-315/316 → бэкенд KAN-318).
 * Копит метрики в очереди и отправляет батчем через navigator.sendBeacon
 * на POST /api/rum-metrics: по visibilitychange:hidden и по таймеру.
 * Приватность: без персональных данных (маршрут-шаблон + значение), уважает DNT.
 * Оверхед: сэмплирование на сессию в проде.
 */

export type RumMetricType =
  "LCP" | "INP" | "CLS" | "TTFB" | "FCP" | "ApiDuration";

interface RumMetricItem {
  metricType: RumMetricType;
  value: number;
  route: string;
  deviceType?: string;
  connectionType?: string;
}

const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE = 50;
// Доля сессий, отправляющих метрики в проде (сессионное решение — один раз).
// KAN-335: пайплайн проверен, диагностический 100% возвращён к штатным 20%.
const PROD_SAMPLE_RATE = 0.2;

let queue: RumMetricItem[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let sessionSampled: boolean | null = null;

function isDntEnabled(): boolean {
  if (typeof navigator === "undefined") return true;
  return (
    navigator.doNotTrack === "1" ||
    (window as { doNotTrack?: string }).doNotTrack === "1"
  );
}

function isSampledSession(): boolean {
  if (sessionSampled !== null) return sessionSampled;
  if (process.env.NODE_ENV !== "production") {
    sessionSampled = true;
    return sessionSampled;
  }
  try {
    const key = "rum:sampled";
    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      sessionSampled = stored === "1";
    } else {
      sessionSampled = Math.random() < PROD_SAMPLE_RATE;
      sessionStorage.setItem(key, sessionSampled ? "1" : "0");
    }
  } catch {
    sessionSampled = false;
  }
  return sessionSampled;
}

/** Сегменты, за которыми в app/ идёт динамический `[param]` (слаг, ник, токен). KAN-612 */
const DYNAMIC_PARENTS = new Set([
  "u",
  "organizations",
  "tournaments",
  "rosters",
  "roster",
  "scrims",
  "predraft",
  "tournament-bracket",
  "matches",
  "apps",
  "plugins",
  "tickets",
  "arena",
  "temp-roster",
  "tournament-card",
]);
/** Статические страницы под теми же родителями — не сворачивать. */
const STATIC_CHILDREN = new Set(["create", "oauth", "my", "test"]);

/**
 * /tournaments/123/register → /tournaments/[id]/register, /u/nick → /u/[id] (агрегируемый шаблон).
 * Режим "api" — для адресов запросов: там слаги страниц не водятся, а под теми же
 * родителями живут статичные эндпоинты (/tournaments/upcoming), поэтому сворачиваем
 * только числа, uuid и длинный hex.
 */
export function normalizeRoute(
  path: string,
  mode: "page" | "api" = "page",
): string {
  const clean = path.split("?")[0]!.split("#")[0]!;
  const normalized = clean
    .split("/")
    .map((seg, i, all) =>
      /^\d+$/.test(seg) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        seg,
      ) ||
      (mode === "api" && /^[0-9a-f]{16,}$/i.test(seg)) ||
      (mode === "page" &&
        seg !== "" &&
        DYNAMIC_PARENTS.has(all[i - 1] ?? "") &&
        !STATIC_CHILDREN.has(seg))
        ? "[id]"
        : seg,
    )
    .join("/");
  return normalized.slice(0, 200) || "/";
}

function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";
  return window.innerWidth < 768
    ? "mobile"
    : window.innerWidth < 1024
      ? "tablet"
      : "desktop";
}

function getConnectionType(): string | undefined {
  const conn = (navigator as { connection?: { effectiveType?: string } })
    .connection;
  return conn?.effectiveType;
}

function flush(): void {
  if (queue.length === 0) return;
  const items = queue;
  queue = [];
  try {
    const payload = JSON.stringify({ items });
    // sendBeacon с Blob application/json — тело парсится [FromBody] на бэке
    const blob = new Blob([payload], { type: "application/json" });
    if (!navigator.sendBeacon("/api/rum-metrics", blob)) {
      // Бекон не встал в очередь (лимиты) — тихо дропаем, RUM не критичен
    }
  } catch {
    // Отправка метрик никогда не должна ломать приложение
  }
}

export function rumEnqueue(
  metricType: RumMetricType,
  value: number,
  route?: string,
): void {
  if (typeof window === "undefined") return;
  if (isDntEnabled() || !isSampledSession()) return;
  if (!Number.isFinite(value) || value < 0) return;

  queue.push({
    metricType,
    value: Math.round(value * 100) / 100,
    route: normalizeRoute(route ?? window.location.pathname),
    deviceType: getDeviceType(),
    connectionType: getConnectionType(),
  });
  if (queue.length >= MAX_QUEUE) flush();

  if (!initialized) {
    initialized = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
    flushTimer = flushTimer ?? setInterval(flush, FLUSH_INTERVAL_MS);
  }
}
